import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { includes, filter, get } from 'lodash';
import getLocalized from 'utils/get-localized';
import { navigateToAuthPage } from 'actions/app';
import { getContractNumberSelector, getIsLoggedInSelector } from 'reducers/profile/selectors';
import { selectIsParentalControlEnabled, selectIsPurchaseProtected } from 'reducers/parental-lock/selectors';
import useBuyDetailsTariff from 'hooks/useBuyDetailsTariff';
import BackButton from 'components/BackButton';
import HorizontalList from 'components/HorizontalList';
import TariffItem from './TariffItem';
import { StyledHeader, StyledContentWrapper } from './StyledComponents';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import useModals from 'hooks/useModals';

const SECTION_NAME = 'DETAILS_TARIFFS';
const SUBS_ITEM = { id: 'subscription', isSubscription: true };

const DetailsTariffsPage = ({ data }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isParentalEnabled = useSelector(selectIsParentalControlEnabled);
  const isPurchaseProtected = useSelector(selectIsPurchaseProtected);
  const contractCode = useSelector(getContractNumberSelector);
  const buyAction = useBuyDetailsTariff(data.id);
  const title = getLocalized(data.titles, get(data, 'titles.UA.title', data.title));
  const isInSubscription = includes(data.types, 'SVOD');
  const { showEnterPinModal } = useModals();

  const onItemEnter = useCallback(
    (item) => {
      const onSuccess = history.location.pathname.replace('/tarrifs', '');
      if (item.isSubscription) {
        history.push({
          pathname: '/profile/packages/filter',
          search: `?vodAssetId=${data.id}&onSuccess=${onSuccess}`,
        });
      } else {
        if (!isLoggedIn) {
          const search = `?vodAssetId=${data.id}&tariffId=${item.id}&onSuccess=${onSuccess}`;
          dispatch(navigateToAuthPage(search));
          return;
        }

        if (isParentalEnabled && isPurchaseProtected) {
          innerAnalyticsVolia.postEvent({
            eventGroup: 'notification',
            eventName: 'PIN request',
            eventValue: 'show notification',
          });
          showEnterPinModal({ sectionToFocus: SECTION_NAME }, () => buyAction({ tariffId: item.id, contractCode }));
          return;
        }

        buyAction({ tariffId: item.id, contractCode });
      }
    },
    [
      buyAction,
      contractCode,
      data.id,
      dispatch,
      history,
      isLoggedIn,
      isParentalEnabled,
      isPurchaseProtected,
      showEnterPinModal,
    ],
  );

  const items = useMemo(() => {
    const promoRent = filter(data.prices, ({ duration, promoAmount }) => !!duration && promoAmount);
    const rent = filter(data.prices, ({ duration, promoAmount }) => !!duration && !promoAmount);
    // only the purchase tariffs have no duration
    const purchase = filter(data.prices, ({ duration }) => !duration);
    // Should be sorted by lower price
    promoRent.sort((a, b) => {
      const aPromo = a.promoAmount || 0;
      const bPromo = b.promoAmount || 0;
      if (aPromo > bPromo) return 1;
      if (aPromo < bPromo) return -1;
      return 0;
    });
    // Should be sorted by lower duration
    rent.sort((a, b) => {
      const aDuration = a.duration || 0;
      const bDuration = b.duration || 0;
      if (aDuration > bDuration) return 1;
      if (aDuration < bDuration) return -1;
      return 0;
    });

    const tariffs = [...promoRent, ...rent, ...purchase];
    /*
     * If the object is also available in the subscription, then need to display
     * the purchase of the subscription as the first element in the tariffs
     */
    if (isInSubscription) tariffs.unshift(SUBS_ITEM);

    return tariffs;
  }, [data.prices, isInSubscription]);

  return (
    <>
      <BackButton onEnter={history.goBack} />
      <StyledHeader>{title}</StyledHeader>
      <StyledContentWrapper>
        <HorizontalList
          items={items}
          itemSize={355}
          onEnter={onItemEnter}
          listId={'details-tariffs'}
          sectionName={SECTION_NAME}
          itemComponent={TariffItem}
          startScrollIndex={2}
          defaultSection
        />
      </StyledContentWrapper>
    </>
  );
};

DetailsTariffsPage.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.object.isRequired,
    titles: PropTypes.object.isRequired,
    types: PropTypes.array,
    prices: PropTypes.array.isRequired,
  }).isRequired,
};

export default DetailsTariffsPage;
